import { type RefObject, useEffect, useRef } from 'react'

/** How much of a message has to be inside the scrolling area: this share of its height, or half the area for a taller one. */
export const SEEN_SHARE = 0.6
/** How long it has to stay there without a break, while the page is visible and focused. */
export const SEEN_AFTER_MILLISECONDS = 1000
/** How often what was seen is passed on, at most. */
const BATCH_EVERY_MILLISECONDS = 1000

/** Fine enough that a tall message's smaller share is crossed close to where it lies. */
const THRESHOLDS = Array.from({ length: 21 }, (_, step) => step / 20)

const looking = () => document.visibilityState === 'visible' && document.hasFocus()

interface Watch {
  root: HTMLElement
  track: (unread: ReadonlySet<string>) => void
  stop: () => void
}

type Report = (ids: string[]) => void

function watch(root: HTMLElement, reportNow: () => Report): Watch {
  const rows = new Map<Element, string>()
  // The rows at the threshold now, each with the timer that reads it, or null while nobody is looking.
  const timers = new Map<Element, number | null>()
  const reported = new Set<string>()
  // Each read goes to the callback it was read under: a room switched away
  // from keeps the root, and a batch outlives the room it was seen in.
  let batch = new Map<Report, string[]>()
  let flushTimer: number | undefined
  let lastFlush = -Infinity

  function flush() {
    window.clearTimeout(flushTimer)
    flushTimer = undefined
    if (!batch.size) return
    lastFlush = Date.now()
    const ready = batch
    batch = new Map()
    for (const [report, ids] of ready) report(ids)
  }

  function clear(row: Element) {
    const timer = timers.get(row)
    if (timer) window.clearTimeout(timer)
  }

  function start(row: Element, id: string) {
    timers.set(row, looking() ? window.setTimeout(() => read(row, id), SEEN_AFTER_MILLISECONDS) : null)
  }

  function forget(row: Element) {
    clear(row)
    timers.delete(row)
    rows.delete(row)
    observer.unobserve(row)
  }

  function read(row: Element, id: string) {
    forget(row)
    reported.add(id)
    const report = reportNow()
    batch.set(report, [...batch.get(report) ?? [], id])
    flushTimer ??= window.setTimeout(flush, Math.max(0, lastFlush + BATCH_EVERY_MILLISECONDS - Date.now()))
  }

  const observer = new IntersectionObserver((entries) => {
    for (const { target, isIntersecting, intersectionRect, boundingClientRect, rootBounds } of entries) {
      const id = rows.get(target)
      if (id === undefined) continue
      const meets = isIntersecting && rootBounds !== null
        && intersectionRect.height >= Math.min(boundingClientRect.height * SEEN_SHARE, rootBounds.height / 2)
      if (meets && !timers.has(target)) start(target, id)
      if (!meets && timers.has(target)) {
        clear(target)
        timers.delete(target)
      }
    }
  }, { root, threshold: THRESHOLDS })

  // Looking away breaks the second, so looking back starts it again.
  function attention() {
    const now = looking()
    for (const [row, id] of rows) {
      const timer = timers.get(row)
      if (!now && timer) {
        window.clearTimeout(timer)
        timers.set(row, null)
      }
      if (now && timer === null) start(row, id)
    }
    // The page may never be shown again, and what was read until now stays read.
    if (document.visibilityState === 'hidden') flush()
  }
  document.addEventListener('visibilitychange', attention)
  window.addEventListener('focus', attention)
  window.addEventListener('blur', attention)

  return {
    root,
    track(unread) {
      for (const [row, id] of rows) if (!unread.has(id) || !root.contains(row)) forget(row)
      for (const row of root.querySelectorAll<HTMLElement>('[data-message-id]')) {
        const id = row.dataset.messageId
        if (id && unread.has(id) && !reported.has(id) && !rows.has(row)) {
          rows.set(row, id)
          observer.observe(row)
        }
      }
    },
    stop() {
      observer.disconnect()
      for (const row of timers.keys()) clear(row)
      document.removeEventListener('visibilitychange', attention)
      window.removeEventListener('focus', attention)
      window.removeEventListener('blur', attention)
      flush()
    },
  }
}

/**
 * Read a message by seeing it: `onSeen` gets the ids of the rows under `root`
 * (each carrying `data-message-id`) whose id is in `unread` once most of the
 * row has been inside `root` for a second while the page is looked at. A
 * fling past a row does not read it, and neither does a window behind
 * another. Ids go out in batches at most once a second, and at once when the
 * page is hidden or the root goes.
 */
export function useSeen(root: RefObject<HTMLElement | null>, unread: ReadonlySet<string>,
                        onSeen: (ids: string[]) => void): void {
  const report = useRef(onSeen)
  report.current = onSeen
  const watching = useRef<Watch | null>(null)

  // After every render, because the rows under the root come and go with the
  // messages and the root itself with the view, and a dependency list sees
  // neither. A row that stays keeps the part of its second it is through.
  useEffect(() => {
    const node = root.current
    if (watching.current?.root !== node) {
      watching.current?.stop()
      watching.current = node && watch(node, () => report.current)
    }
    watching.current?.track(unread)
  })

  useEffect(() => () => {
    watching.current?.stop()
    watching.current = null
  }, [])
}
